import supabase from './supabase';

class UsageService {
  /**
   * Get current month's usage for an organization
   * @param {string} organizationId - The organization ID
   * @returns {Promise<{success: boolean, data?: object, error?: string}>}
   */
  async getCurrentMonthUsage(organizationId) {
    try {
      const { data, error } = await supabase
        .rpc('get_current_month_usage', { org_id: organizationId });

      if (error) {
        console.error('Error getting current usage:', error);
        return { success: false, error: error.message };
      }

      // The RPC returns an array, get the first (and only) result
      const usage = data?.[0] || { ai_reviews_used: 0, ai_reviews_limit: 0, month_year: '' };

      return { 
        success: true, 
        data: {
          used: usage.ai_reviews_used,
          limit: usage.ai_reviews_limit,
          monthYear: usage.month_year,
          isUnlimited: usage.ai_reviews_limit === null,
          canGenerate: usage.ai_reviews_limit === null || usage.ai_reviews_used < usage.ai_reviews_limit
        }
      };
    } catch (error) {
      console.error('Error in getCurrentMonthUsage:', error);
      return { success: false, error: 'Failed to get usage data' };
    }
  }

  /**
   * Check if organization can generate an AI review (usage limit check)
   * @param {string} organizationId - The organization ID
   * @returns {Promise<{success: boolean, canGenerate?: boolean, usage?: object, error?: string}>}
   */
  async canGenerateAIReview(organizationId) {
    try {
      const usageResult = await this.getCurrentMonthUsage(organizationId);
      
      if (!usageResult.success) {
        return { success: false, error: usageResult.error };
      }

      const { used, limit, isUnlimited } = usageResult.data;
      const canGenerate = isUnlimited || used < limit;

      return {
        success: true,
        canGenerate,
        usage: usageResult.data,
        reason: canGenerate ? null : `Monthly limit of ${limit} AI reviews reached`
      };
    } catch (error) {
      console.error('Error in canGenerateAIReview:', error);
      return { success: false, error: 'Failed to check usage limits' };
    }
  }

  /**
   * Increment AI review usage (call this when generating an AI review)
   * @param {string} organizationId - The organization ID
   * @returns {Promise<{success: boolean, data?: object, error?: string}>}
   */
  async incrementAIReviewUsage(organizationId) {
    try {
      const { data, error } = await supabase
        .rpc('increment_ai_review_usage', { org_id: organizationId });

      if (error) {
        console.error('Error incrementing usage:', error);
        return { success: false, error: error.message };
      }

      // The function returns a boolean indicating if the increment was successful
      if (!data) {
        return { 
          success: false, 
          error: 'Usage limit reached. Cannot generate more AI reviews this month.' 
        };
      }

      // Get updated usage after increment
      const updatedUsage = await this.getCurrentMonthUsage(organizationId);
      
      return { 
        success: true, 
        data: updatedUsage.success ? updatedUsage.data : null
      };
    } catch (error) {
      console.error('Error in incrementAIReviewUsage:', error);
      return { success: false, error: 'Failed to update usage counter' };
    }
  }

  /**
   * Get organization's subscription plan and limits
   * @param {string} organizationId - The organization ID
   * @returns {Promise<{success: boolean, data?: object, error?: string}>}
   */
  async getOrganizationPlan(organizationId) {
    try {
      const { data, error } = await supabase
        .from('organization_subscriptions')
        .select(`
          *,
          plan:subscription_plans(*)
        `)
        .eq('organization_id', organizationId)
        .eq('status', 'active')
        .single();

      if (error) {
        // If no subscription exists, default to free plan
        if (error.code === 'PGRST116') {
          return await this.getDefaultFreePlan();
        }
        console.error('Error getting organization plan:', error);
        return { success: false, error: error.message };
      }

      return {
        success: true,
        data: {
          subscription: data,
          plan: data.plan,
          isFreePlan: data.plan?.name === 'free',
          isProPlan: data.plan?.name === 'pro',
          monthlyLimit: data.plan?.monthly_review_limit,
          isUnlimited: data.plan?.monthly_review_limit === null
        }
      };
    } catch (error) {
      console.error('Error in getOrganizationPlan:', error);
      return { success: false, error: 'Failed to get organization plan' };
    }
  }

  /**
   * Get default free plan for organizations without subscriptions
   * @returns {Promise<{success: boolean, data?: object, error?: string}>}
   */
  async getDefaultFreePlan() {
    try {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('name', 'free')
        .single();

      if (error) {
        console.error('Error getting free plan:', error);
        return { success: false, error: error.message };
      }

      return {
        success: true,
        data: {
          subscription: null,
          plan: data,
          isFreePlan: true,
          isProPlan: false,
          monthlyLimit: data.monthly_review_limit,
          isUnlimited: false
        }
      };
    } catch (error) {
      console.error('Error in getDefaultFreePlan:', error);
      return { success: false, error: 'Failed to get default plan' };
    }
  }

  /**
   * Get usage history for an organization
   * @param {string} organizationId - The organization ID
   * @param {number} monthCount - Number of months to retrieve (default: 6)
   * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
   */
  async getUsageHistory(organizationId, monthCount = 6) {
    try {
      const { data, error } = await supabase
        .from('monthly_usage')
        .select('*')
        .eq('organization_id', organizationId)
        .order('month_year', { ascending: false })
        .limit(monthCount);

      if (error) {
        console.error('Error getting usage history:', error);
        return { success: false, error: error.message };
      }

      return {
        success: true,
        data: data.map(usage => ({
          monthYear: usage.month_year,
          used: usage.ai_reviews_used,
          limit: usage.ai_reviews_limit,
          isUnlimited: usage.ai_reviews_limit === null,
          resetDate: usage.reset_date
        }))
      };
    } catch (error) {
      console.error('Error in getUsageHistory:', error);
      return { success: false, error: 'Failed to get usage history' };
    }
  }

  /**
   * Format usage data for display
   * @param {object} usage - Usage data from getCurrentMonthUsage
   * @returns {object} Formatted usage display data
   */
  formatUsageDisplay(usage) {
    if (!usage) return { display: 'No usage data', percentage: 0 };

    if (usage.isUnlimited) {
      return {
        display: `${usage.used} reviews used (Unlimited)`,
        percentage: 0, // No percentage for unlimited
        status: 'unlimited'
      };
    }

    const percentage = usage.limit > 0 ? Math.round((usage.used / usage.limit) * 100) : 0;
    
    return {
      display: `${usage.used}/${usage.limit} reviews used`,
      percentage,
      status: percentage >= 100 ? 'limit_reached' : percentage >= 80 ? 'warning' : 'normal'
    };
  }

  /**
   * Check if organization needs to upgrade (for conversion prompts)
   * @param {object} usage - Usage data from getCurrentMonthUsage
   * @returns {boolean} True if organization should see upgrade prompts
   */
  shouldShowUpgradePrompt(usage) {
    if (!usage || usage.isUnlimited) return false;
    
    // Show upgrade prompt if they've used 80% or more of their limit
    return usage.limit > 0 && usage.used >= (usage.limit * 0.8);
  }
}

export default new UsageService();